/* Ops portal: dispatch board (assign/reassign), issues inbox, attendance, callbacks. */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  LayoutDashboard, AlertTriangle, Clock4, PhoneCall, MapPin, CheckCircle2, ImageIcon,
} from "lucide-react";
import config from "../../config";
import { formatDateDMY, formatDateTimeDMY } from "../../lib/dates";
import DmyDateInput from "../../components/DmyDateInput";
import {
  useStaffSession, StaffLogin, StaffShell, SectionCard, StatCard, StatusChip,
  staffHeaders, detailMessage, openAuthedFile,
} from "./staffShared";

function todayStr() {
  const now = new Date();
  const ist = new Date(now.getTime() + (330 + now.getTimezoneOffset()) * 60000);
  return ist.toISOString().slice(0, 10);
}

function fmtDateTime(iso) {
  return formatDateTimeDMY(iso);
}

/* ── Dispatch board ─────────────────────────────────────────────────────── */

function AssignControls({ b, nurses, doctors, onAssigned, setError }) {
  const [nurseId, setNurseId] = useState(b.nurse_id || "");
  const [doctorId, setDoctorId] = useState(b.assigned_doctor_id || b.doctor_id || "");
  const [saving, setSaving] = useState(false);

  const assign = async () => {
    setSaving(true);
    setError("");
    try {
      const body = {};
      if (nurseId && nurseId !== b.nurse_id) body.nurse_id = nurseId;
      if (doctorId && doctorId !== (b.assigned_doctor_id || b.doctor_id)) body.doctor_id = doctorId;
      if (Object.keys(body).length) {
        await axios.patch(`${config.API}/ops/bookings/${b.id}/assign`, body, { headers: staffHeaders() });
        onAssigned();
      }
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mt-3">
      <select value={nurseId} onChange={(e) => setNurseId(e.target.value)}
        className="rounded-lg border border-[#673c96]/20 px-2.5 py-1.5 text-xs bg-white max-w-[180px]"
        data-testid={`assign-nurse-${b.id}`}>
        <option value="">Nurse…</option>
        {nurses.map((n) => (
          <option key={n.id} value={n.id}>
            {n.full_name}{n.available_hours?.length ? ` (${n.available_hours.length}h avail)` : ""}{n.checked_in ? " ✓in" : ""}
          </option>
        ))}
      </select>
      <select value={doctorId} onChange={(e) => setDoctorId(e.target.value)}
        className="rounded-lg border border-[#673c96]/20 px-2.5 py-1.5 text-xs bg-white max-w-[160px]"
        data-testid={`assign-doctor-${b.id}`}>
        <option value="">Doctor…</option>
        {doctors.map((d) => <option key={d.id} value={d.id}>{d.full_name}</option>)}
      </select>
      <button type="button" onClick={assign} disabled={saving}
        className="px-3 py-1.5 rounded-full bg-[#673c96] text-white text-xs font-semibold"
        data-testid={`assign-save-${b.id}`}>
        {saving ? "…" : b.nurse_id ? "Reassign" : "Assign"}
      </button>
    </div>
  );
}

function OpsBookingCard({ b, nurses, doctors, onChanged, setError }) {
  const setStatus = async (status) => {
    setError("");
    try {
      await axios.post(`${config.API}/ops/bookings/${b.id}/status`, { status }, { headers: staffHeaders() });
      onChanged();
    } catch (err) {
      setError(detailMessage(err));
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#673c96]/10 p-4" data-testid={`ops-booking-${b.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-semibold text-[#311b52]" style={{ fontFamily: "Poppins" }}>
          {b.preferred_slot || "—"} · {formatDateDMY(b.preferred_date)} · {b.beneficiary_name || b.customer_name}
        </p>
        <StatusChip status={b.status} />
      </div>
      <p className="text-sm text-[#673c96] mt-0.5">{b.service_name || b.vaccine_name}</p>
      <p className="text-xs text-[#6b7280] mt-1 flex items-center gap-1">
        <MapPin className="w-3.5 h-3.5 shrink-0" /> {(b.address || "—").slice(0, 70)}
      </p>
      <div className="text-xs text-[#6b7280] mt-1">
        {b.customer_name} · +{b.customer_phone || "—"}
        {b.nurse_name ? <span className="ml-2 text-[#673c96] font-semibold">Nurse: {b.nurse_name}</span> : null}
        {b.doctor_name ? <span className="ml-2 text-[#673c96] font-semibold">Dr: {b.doctor_name}</span> : null}
      </div>

      {["pending_confirmation", "confirmed", "assigned"].includes(b.status) && (
        <AssignControls b={b} nurses={nurses} doctors={doctors} onAssigned={onChanged} setError={setError} />
      )}

      <div className="flex flex-wrap gap-2 mt-2.5">
        {b.status === "pending_confirmation" && (
          <button type="button" onClick={() => setStatus("confirmed")}
            className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold">Confirm</button>
        )}
        {["pending_confirmation", "confirmed", "assigned"].includes(b.status) && (
          <button type="button" onClick={() => { if (window.confirm("Cancel this booking?")) setStatus("cancelled"); }}
            className="px-3 py-1.5 rounded-full border border-[#ee4036]/40 text-[#ee4036] text-xs font-semibold">Cancel</button>
        )}
      </div>
    </div>
  );
}

function BoardSection() {
  const [date, setDate] = useState(todayStr());
  const [board, setBoard] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    axios.get(`${config.API}/ops/board`, { params: { date }, headers: staffHeaders() })
      .then(({ data }) => setBoard(data))
      .catch((err) => setError(detailMessage(err)));
  }, [date]);

  useEffect(() => { load(); }, [load]);

  if (!board) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>;
  }

  const sections = [
    ["pending", "Pending confirmation"],
    ["to_assign", "Confirmed — needs nurse"],
    ["scheduled", "Scheduled"],
    ["ongoing", "Ongoing visits"],
    ["completed", "Completed"],
    ["cancelled", "Cancelled"],
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-44">
          <DmyDateInput value={date} onChange={setDate} className="rounded-xl border border-[#673c96]/20 px-3 py-2 text-sm bg-white" data-testid="board-date" />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 flex-1 min-w-[280px]">
          <StatCard label="Pending" value={board.kpis.pending} />
          <StatCard label="To assign" value={board.kpis.to_assign} accent />
          <StatCard label="Scheduled" value={board.kpis.scheduled} />
          <StatCard label="Ongoing" value={board.kpis.ongoing} />
          <StatCard label="Issues" value={board.kpis.open_issues} />
        </div>
      </div>
      {error && <p className="text-sm text-[#ee4036]">{error}</p>}

      {sections.map(([key, label]) => {
        const items = board.groups[key] || [];
        if (!items.length && !["pending", "to_assign"].includes(key)) return null;
        return (
          <SectionCard key={key} title={`${label} (${items.length})`}>
            {items.length === 0 ? (
              <p className="text-sm text-[#6b7280]">Nothing here.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                {items.map((b) => (
                  <OpsBookingCard key={b.id} b={b} nurses={board.nurses || []} doctors={board.doctors || []}
                    onChanged={load} setError={setError} />
                ))}
              </div>
            )}
          </SectionCard>
        );
      })}
    </div>
  );
}

/* ── Issues inbox ───────────────────────────────────────────────────────── */

function IssuesSection() {
  const [tab, setTab] = useState("open");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${config.API}/ops/issues`, { params: { status: tab }, headers: staffHeaders() })
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => setError(detailMessage(err)))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    const note = window.prompt("Resolution note (optional):") || "";
    try {
      await axios.post(`${config.API}/ops/issues/${id}/resolve`, { note }, { headers: staffHeaders() });
      load();
    } catch (err) {
      setError(detailMessage(err));
    }
  };

  return (
    <div className="space-y-4">
      <SectionCard title="Issues reported by nurses">
        <div className="flex gap-2">
          {[["open", "Open"], ["resolved", "Resolved"]].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === id ? "bg-[#311b52] text-white" : "bg-[#f4f5f7] text-[#673c96]"}`}>
              {label}
            </button>
          ))}
        </div>
      </SectionCard>
      {error && <p className="text-sm text-[#ee4036]">{error}</p>}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-[#673c96]/25 p-10 text-center text-[#673c96]">
          No {tab} issues.
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((i) => (
            <div key={i.id} className="bg-white rounded-2xl border border-[#673c96]/10 p-4" data-testid={`issue-${i.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="font-semibold text-[#311b52]">{i.nurse_name || "Nurse"} · {i.service_name || "Booking"}</p>
                <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${i.status === "open" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {i.status}
                </span>
              </div>
              <p className="text-sm text-[#311b52] mt-2">{i.description}</p>
              <p className="text-xs text-[#6b7280] mt-1">
                {i.beneficiary_name || i.customer_name || ""} · Booking {i.booking_id} · {fmtDateTime(i.created_at)}
              </p>
              {i.resolution_note && <p className="text-xs text-emerald-700 mt-1">Resolution: {i.resolution_note}</p>}
              <div className="flex gap-2 mt-3">
                {i.image_ref && (
                  <button type="button" onClick={() => openAuthedFile(`${config.API}/staff/files/${i.image_ref}`, setError)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#673c96]/30 text-[#673c96] text-xs font-semibold">
                    <ImageIcon className="w-3.5 h-3.5" /> View photo
                  </button>
                )}
                {i.status === "open" && (
                  <button type="button" onClick={() => resolve(i.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold"
                    data-testid={`resolve-${i.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Mark resolved
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Attendance view ────────────────────────────────────────────────────── */

function OpsAttendanceSection() {
  const [date, setDate] = useState(todayStr());
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${config.API}/ops/attendance`, { params: { date }, headers: staffHeaders() })
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => setError(detailMessage(err)));
  }, [date]);

  return (
    <SectionCard title="Staff attendance"
      actions={<div className="w-44">
        <DmyDateInput value={date} onChange={setDate} className="rounded-xl border border-[#673c96]/20 px-3 py-2 text-sm" data-testid="ops-attendance-date" />
      </div>}>
      {error && <p className="text-sm text-[#ee4036] mb-2">{error}</p>}
      {items.length === 0 ? (
        <p className="text-sm text-[#6b7280]">No check-ins on this date.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#673c96]/10 text-left text-xs text-[#673c96]">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Check in</th>
                <th className="py-2 pr-4">Check out</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={`${a.nurse_id}-${a.date}`} className="border-b border-[#673c96]/5">
                  <td className="py-2.5 pr-4 font-medium text-[#311b52]">{a.nurse_name || a.nurse_id}</td>
                  <td className="py-2.5 pr-4 text-[#6b7280]">{a.check_in_at ? fmtDateTime(a.check_in_at) : "—"}</td>
                  <td className="py-2.5 pr-4 text-[#6b7280]">{a.check_out_at ? fmtDateTime(a.check_out_at) : "—"}</td>
                  <td className="py-2.5">
                    {a.check_in_at && a.check_out_at
                      ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Present</span>
                      : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">On duty</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

/* ── Callbacks ──────────────────────────────────────────────────────────── */

function CallbacksSection() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [drafts, setDrafts] = useState({});

  const load = useCallback(() => {
    axios.get(`${config.API}/ops/callbacks`, { headers: staffHeaders() })
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => setError(detailMessage(err)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    try {
      await axios.patch(`${config.API}/ops/callbacks/${id}`, body, { headers: staffHeaders() });
      load();
    } catch (err) {
      setError(detailMessage(err));
    }
  };

  return (
    <SectionCard title="Open callbacks" subtitle="Website visitors who asked for a call back. Flag a lead as Follow-Up to return later.">
      {error && <p className="text-sm text-[#ee4036] mb-2">{error}</p>}
      {items.length === 0 ? (
        <p className="text-sm text-[#6b7280]">No open callbacks. 🎉</p>
      ) : (
        <div className="divide-y divide-[#673c96]/10">
          {items.map((c) => {
            const remarks = drafts[c.id] !== undefined ? drafts[c.id] : (c.remarks || "");
            return (
            <div key={c.id} className="py-3 flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-[180px]">
                <p className="font-medium text-[#311b52]">{c.name} · +{c.phone}</p>
                <p className="text-xs text-[#6b7280]">{c.vaccine_interest || "General enquiry"} · {fmtDateTime(c.created_at)}</p>
                {c.status === "follow_up" && (
                  <p className="text-[11px] font-semibold text-amber-700 mt-1">Follow-up{c.follow_up_at ? ` · ${fmtDateTime(c.follow_up_at)}` : ""}</p>
                )}
              </div>
              <div className="flex-1 min-w-[200px] max-w-sm">
                <textarea
                  rows={2}
                  value={remarks}
                  placeholder="Remarks for later follow-up…"
                  onChange={(e) => setDrafts((d) => ({ ...d, [c.id]: e.target.value }))}
                  className="w-full rounded-lg border border-[#673c96]/20 px-2 py-1.5 text-xs mb-1.5"
                />
                <button type="button" onClick={() => patch(c.id, { remarks })} className="text-xs font-semibold text-[#673c96]">Save remarks</button>
              </div>
              <div className="flex gap-2">
                <a href={`tel:+${c.phone}`} className="px-3 py-1.5 rounded-full border border-[#673c96]/30 text-[#673c96] text-xs font-semibold inline-flex items-center gap-1">
                  <PhoneCall className="w-3.5 h-3.5" /> Call
                </a>
                {c.status !== "follow_up" && (
                  <button type="button" onClick={() => patch(c.id, { status: "follow_up", remarks })}
                    className="px-3 py-1.5 rounded-full border border-amber-300 text-amber-800 text-xs font-semibold">
                    Follow-Up
                  </button>
                )}
                <button type="button" onClick={() => patch(c.id, { status: "contacted", remarks })}
                  className="px-3 py-1.5 rounded-full bg-[#673c96] text-white text-xs font-semibold">
                  Mark contacted
                </button>
              </div>
            </div>
          );
          })}
        </div>
      )}
    </SectionCard>
  );
}

/* ── Portal root ────────────────────────────────────────────────────────── */

export default function OpsPortal() {
  const session = useStaffSession("ops");
  const [tab, setTab] = useState("board");

  if (session.status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f5f7]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" />
      </div>
    );
  }

  if (session.status === "login") {
    return (
      <StaffLogin role="ops" title="Ops portal" onAuthed={session.onAuthed}
        subtitle="Dispatch bookings, resolve nurse issues and track attendance." />
    );
  }

  const nav = [
    { id: "board", label: "Dispatch board", icon: LayoutDashboard },
    { id: "issues", label: "Issues", icon: AlertTriangle },
    { id: "attendance", label: "Attendance", icon: Clock4 },
    { id: "callbacks", label: "Callbacks", icon: PhoneCall },
  ];

  return (
    <StaffShell roleLabel="Ops" user={session.user} nav={nav} active={tab} onNavigate={setTab} onLogout={session.logout} portalPath="/ops">
      {tab === "board" && <BoardSection />}
      {tab === "issues" && <IssuesSection />}
      {tab === "attendance" && <OpsAttendanceSection />}
      {tab === "callbacks" && <CallbacksSection />}
    </StaffShell>
  );
}
